'use client';

import ScrollReveal from '@/components/ScrollReveal';

const values = [
  {
    number: '01',
    title: 'Radical Transparency',
    description:
      'Full ingredient disclosure on every product, including sourcing origin. No hidden fragrance blends. No mystery complexes. Just science you can read.',
    metric: '100%',
    metricLabel: 'Ingredient Transparency',
  },
  {
    number: '02',
    title: 'Clinical Efficacy',
    description:
      'Every formula is validated in independent clinical trials before launch. We only launch products that show measurable results in peer-reviewed studies.',
    metric: '94%',
    metricLabel: 'Avg. Visible Improvement',
  },
  {
    number: '03',
    title: 'Ethical Supply Chain',
    description:
      'We audit every supplier annually. Fair wages, safe conditions, and environmental responsibility are non-negotiable requirements, not aspirations.',
    metric: '12',
    metricLabel: 'Certified Organic Partners',
  },
  {
    number: '04',
    title: 'Circular Packaging',
    description:
      'All primary packaging is recyclable glass or aluminum. Our mailers are compostable. We offset 110% of our shipping carbon through verified reforestation.',
    metric: '0',
    metricLabel: 'Single-Use Plastic Used',
  },
];

export default function ValuesSection() {
  return (
    <section className="section-padding bg-[#F0EBE0]">
      <div className="max-w-8xl mx-auto px-6 lg:px-10">
        <ScrollReveal>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-8 h-px bg-[#C9A96E]" />
            <span className="label-gold">What We Stand For</span>
          </div>
          <div className="flex flex-col lg:flex-row items-start lg:items-end justify-between mb-16 gap-6">
            <h2
              className="font-fraunces leading-tight text-[#1A1612]"
              style={{ fontSize: 'clamp(2.2rem, 4vw, 3.8rem)', fontWeight: 400 }}
            >
              Four commitments
              <br />
              <em className="gradient-gold not-italic">we never compromise</em>
            </h2>
            <p className="text-[14px] text-[#8A7E72] max-w-sm leading-relaxed">
              These are not marketing promises. They are legally binding standards we hold ourselves
              to, audited annually by third-party organizations.
            </p>
          </div>
        </ScrollReveal>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {values.map((value, index) => (
            <ScrollReveal key={value.number} delay={index * 80}>
              <div
                className="group p-8 lg:p-10 transition-all duration-500 hover:shadow-lg cursor-default"
                style={{
                  background: 'white',
                  border: '1px solid rgba(201,169,110,0.1)',
                  borderTop: '2px solid rgba(201,169,110,0.3)',
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLDivElement).style.borderTopColor = '#C9A96E';
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLDivElement).style.borderTopColor =
                    'rgba(201,169,110,0.3)';
                }}
              >
                <div className="flex items-start justify-between mb-6">
                  <span
                    className="font-fraunces text-[#C9A96E] opacity-30 group-hover:opacity-100 transition-opacity leading-none"
                    style={{ fontSize: '2.5rem', fontWeight: 300 }}
                  >
                    {value.number}
                  </span>
                  <div className="text-right">
                    <p
                      className="font-fraunces text-[#C9A96E] leading-none"
                      style={{ fontSize: '2rem', fontWeight: 500 }}
                    >
                      {value.metric}
                    </p>
                    <p className="text-[9px] font-bold tracking-widest uppercase text-[#8A7E72] mt-0.5">
                      {value.metricLabel}
                    </p>
                  </div>
                </div>
                <h3 className="font-sans font-semibold text-[#1A1612] text-[17px] mb-3 tracking-tight">
                  {value.title}
                </h3>
                <p className="text-[14px] text-[#8A7E72] leading-relaxed">{value.description}</p>
                <div className="mt-6 w-8 h-px bg-[#C9A96E] group-hover:w-16 transition-all duration-500" />
              </div>
            </ScrollReveal>
          ))}
        </div>
      </div>
    </section>
  );
}
